import React, { useState, Children } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
interface CardData {
  id: string;
  type: 'DEBIT' | 'CREDIT';
  tier: string;
  gradient: string;
  number: string;
  cardholder: string;
  expires: string;
  limitLabel: string;
  limit: string;
  balanceLabel: string;
  balance: string;
  pattern: 'circles' | 'lines' | 'geometric';
}
const CARDS: CardData[] = [{
  id: 'card-1',
  type: 'DEBIT',
  tier: 'Classic',
  gradient: 'from-[#661806] to-[#3D1A0A]',
  number: '•••• •••• •••• 4892',
  cardholder: 'KASHISH',
  expires: '12/28',
  limitLabel: 'Daily Limit',
  limit: 'Rs 200,000',
  balanceLabel: 'Balance',
  balance: 'Rs 100,000',
  pattern: 'circles'
}, {
  id: 'card-2',
  type: 'CREDIT',
  tier: 'Gold',
  gradient: 'from-[#B8860B] to-[#8B6914]',
  number: '•••• •••• •••• 7231',
  cardholder: 'KASHISH',
  expires: '06/27',
  limitLabel: 'Credit Limit',
  limit: 'Rs 500,000',
  balanceLabel: 'Available Credit',
  balance: 'Rs 350,000',
  pattern: 'lines'
}, {
  id: 'card-3',
  type: 'DEBIT',
  tier: 'Platinum',
  gradient: 'from-[#1A1A2E] to-[#16213E]',
  number: '•••• •••• •••• 5567',
  cardholder: 'KASHISH',
  expires: '03/29',
  limitLabel: 'Daily Limit',
  limit: 'Rs 500,000',
  balanceLabel: 'Balance',
  balance: 'Rs 275,000',
  pattern: 'geometric'
}];
export function CardsScreen() {
  const [activeIndex, setActiveIndex] = useState(0);
  // Track settings per card
  const [frozenStates, setFrozenStates] = useState<Record<string, boolean>>({});
  const [intlStates, setIntlStates] = useState<Record<string, boolean>>({
    'card-1': true,
    'card-2': true,
    'card-3': true
  });
  const activeCard = CARDS[activeIndex];
  const isFrozen = frozenStates[activeCard.id] || false;
  const isInternational = intlStates[activeCard.id] || false;
  const toggleFrozen = () => {
    setFrozenStates(prev => ({
      ...prev,
      [activeCard.id]: !prev[activeCard.id]
    }));
  };
  const toggleInternational = () => {
    setIntlStates(prev => ({
      ...prev,
      [activeCard.id]: !prev[activeCard.id]
    }));
  };
  const handleDragEnd = (e: any, info: PanInfo) => {
    const swipeThreshold = 50;
    if (info.offset.x < -swipeThreshold && activeIndex < CARDS.length - 1) {
      setActiveIndex(activeIndex + 1);
    } else if (info.offset.x > swipeThreshold && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };
  const renderPattern = (pattern: string) => {
    switch (pattern) {
      case 'circles':
        return <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden rounded-[24px]" data-id="element-23">
            <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[80%] rounded-full border border-white/30" data-id="element-24" />
            <div className="absolute bottom-[-30%] left-[-10%] w-[70%] h-[90%] rounded-full border border-white/20" data-id="element-25" />
          </div>;
      case 'lines':
        return <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden rounded-[24px]" data-id="element-26">
            <div className="absolute top-0 right-0 w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, white 10px, white 11px)'
          }} data-id="element-27" />
          </div>;
      case 'geometric':
        return <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden rounded-[24px]" data-id="element-28">
            <div className="absolute top-[-20%] right-[-20%] w-[80%] h-[80%] rotate-45 bg-white" data-id="element-29" />
            <div className="absolute bottom-[-20%] left-[-20%] w-[60%] h-[60%] rotate-45 bg-white" data-id="element-30" />
          </div>;
      default:
        return null;
    }
  };
  return <motion.div initial="hidden" animate="show" variants={stagger} className="w-full min-h-screen pb-24 overflow-x-hidden" style={{
    backgroundColor: '#FFF7F3'
  }} data-id="element-31">
      {/* Header */}
      <motion.div variants={fadeUp} className="px-6 pt-8 pb-4" data-id="element-32">
        <h1 className="text-2xl font-bold text-[#303030]" data-id="element-33">Card Management</h1>
      </motion.div>

      <div className="space-y-8" data-id="element-34">
        {/* Swipeable Cards */}
        <motion.div variants={fadeUp} className="relative w-full" data-id="element-35">
          <div className="overflow-hidden px-6 pb-4 pt-2" data-id="element-36">
            <motion.div className="flex gap-4" drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.2} onDragEnd={handleDragEnd} animate={{
            x: `calc(-${activeIndex * 100}% - ${activeIndex * 16}px)`
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-37">
              {CARDS.map((card, index) => <div key={card.id} className="min-w-full shrink-0" data-id="element-38">
                  <motion.div className={`bg-gradient-to-br ${card.gradient} rounded-[24px] p-6 shadow-xl aspect-[1.586/1] relative`} animate={{
                scale: activeIndex === index ? 1 : 0.95,
                opacity: activeIndex === index ? 1 : 0.7
              }} transition={{
                duration: 0.3
              }} data-id="element-39">
                    {renderPattern(card.pattern)}

                    <div className="relative z-10 h-full flex flex-col justify-between" data-id="element-40">
                      <div className="flex items-start justify-between" data-id="element-41">
                        <div className="flex flex-col gap-2" data-id="element-42">
                          <div className="w-12 h-8 bg-gradient-to-r from-yellow-200 to-yellow-500 rounded-md opacity-90 shadow-sm" data-id="element-43" />
                          <span className="text-white/80 text-[10px] font-bold tracking-widest uppercase bg-white/10 px-2 py-0.5 rounded backdrop-blur-sm w-fit" data-id="element-44">
                            {card.type}
                          </span>
                        </div>
                        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP" className="h-6 opacity-90 brightness-0 invert" data-id="element-45" />
                      </div>

                      <div data-id="element-46">
                        <p className="text-white/70 text-xs font-medium mb-1 tracking-wider uppercase" data-id="element-47">
                          Card Number
                        </p>
                        <p className="text-white text-[22px] font-bold tracking-[0.15em] mb-5 drop-shadow-sm" data-id="element-48">
                          {card.number}
                        </p>

                        <div className="flex items-end justify-between" data-id="element-49">
                          <div data-id="element-50">
                            <p className="text-white/70 text-[10px] font-medium mb-1 tracking-wider uppercase" data-id="element-51">
                              Cardholder
                            </p>
                            <p className="text-white text-[15px] font-bold tracking-wide uppercase" data-id="element-52">
                              {card.cardholder}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-53">
                            <p className="text-white/70 text-[10px] font-medium mb-1 tracking-wider uppercase" data-id="element-54">
                              Expires
                            </p>
                            <p className="text-white text-[15px] font-bold tracking-wide" data-id="element-55">
                              {card.expires}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>)}
            </motion.div>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-2" data-id="element-56">
            {CARDS.map((_, idx) => <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${activeIndex === idx ? 'w-6 bg-[#F37024]' : 'w-1.5 bg-[#E8E0DB]'}`} data-id="element-57" />)}
          </div>
        </motion.div>

        <div className="px-6 space-y-8" data-id="element-58">
          {/* Card Info Section */}
          <motion.div variants={fadeUp} className="bg-white rounded-[24px] p-5 shadow-sm border border-[#F0E8E3]" data-id="element-59">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#F0E8E3]" data-id="element-60">
              <div data-id="element-61">
                <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider mb-1" data-id="element-62">
                  Card Type
                </p>
                <p className="text-[15px] font-bold text-[#303030]" data-id="element-63">
                  {activeCard.tier} {activeCard.type}
                </p>
              </div>
              <div className="text-right" data-id="element-64">
                <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider mb-1" data-id="element-65">
                  Ending In
                </p>
                <p className="text-[15px] font-bold text-[#303030]" data-id="element-66">
                  {activeCard.number.slice(-4)}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-67">
              <div data-id="element-68">
                <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider mb-1" data-id="element-69">
                  {activeCard.balanceLabel}
                </p>
                <p className="text-[18px] font-bold text-[#661806]" data-id="element-70">
                  {activeCard.balance}
                </p>
              </div>
              <div className="text-right" data-id="element-71">
                <p className="text-[11px] font-bold text-[#818285] uppercase tracking-wider mb-1" data-id="element-72">
                  {activeCard.limitLabel}
                </p>
                <p className="text-[15px] font-bold text-[#303030]" data-id="element-73">
                  {activeCard.limit}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Settings List */}
          <motion.div variants={fadeUp} data-id="element-74">
            <h3 className="text-lg font-bold text-[#303030] mb-4" data-id="element-75">
              Card Settings
            </h3>
            <div className="bg-white rounded-[24px] shadow-sm border border-[#F0E8E3] overflow-hidden" data-id="element-76">
              {/* Freeze Card */}
              <div className="flex items-center justify-between p-5 border-b border-[#F0E8E3]" data-id="element-77">
                <div data-id="element-78">
                  <p className="text-[15px] font-bold text-[#303030]" data-id="element-79">
                    Freeze Card
                  </p>
                  <p className="text-[12px] font-medium text-[#818285] mt-0.5" data-id="element-80">
                    Temporarily disable this card
                  </p>
                </div>
                <button onClick={toggleFrozen} className={`w-12 h-7 rounded-full transition-colors relative flex items-center px-1 ${isFrozen ? 'bg-[#F37024]' : 'bg-[#E8E0DB]'}`} data-id="element-81">
                  <motion.div className="w-5 h-5 bg-white rounded-full shadow-sm" animate={{
                  x: isFrozen ? 20 : 0
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-82" />
                </button>
              </div>

              {/* Change PIN */}
              <button className="w-full flex items-center justify-between p-5 border-b border-[#F0E8E3] hover:bg-[#FFF7F3]/50 transition-colors text-left" data-id="element-83">
                <div data-id="element-84">
                  <p className="text-[15px] font-bold text-[#303030]" data-id="element-85">
                    Change PIN
                  </p>
                  <p className="text-[12px] font-medium text-[#818285] mt-0.5" data-id="element-86">
                    Update this card's PIN code
                  </p>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-[#818285]" data-id="element-87" />
              </button>

              {/* Set Limits */}
              <button className="w-full flex items-center justify-between p-5 border-b border-[#F0E8E3] hover:bg-[#FFF7F3]/50 transition-colors text-left" data-id="element-88">
                <div data-id="element-89">
                  <p className="text-[15px] font-bold text-[#303030]" data-id="element-90">
                    Set Limits
                  </p>
                  <p className="text-[12px] font-medium text-[#818285] mt-0.5" data-id="element-91">
                    Manage daily transaction limits
                  </p>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-[#818285]" data-id="element-92" />
              </button>

              {/* View Transactions */}
              <button className="w-full flex items-center justify-between p-5 border-b border-[#F0E8E3] hover:bg-[#FFF7F3]/50 transition-colors text-left" data-id="element-93">
                <div data-id="element-94">
                  <p className="text-[15px] font-bold text-[#303030]" data-id="element-95">
                    View Transactions
                  </p>
                  <p className="text-[12px] font-medium text-[#818285] mt-0.5" data-id="element-96">
                    See activity for this card
                  </p>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-[#818285]" data-id="element-97" />
              </button>

              {/* Activate International Usage */}
              <div className="flex items-center justify-between p-5" data-id="element-98">
                <div data-id="element-99">
                  <p className="text-[15px] font-bold text-[#303030]" data-id="element-100">
                    International Usage
                  </p>
                  <p className="text-[12px] font-medium text-[#818285] mt-0.5" data-id="element-101">
                    Enable for foreign transactions
                  </p>
                </div>
                <button onClick={toggleInternational} className={`w-12 h-7 rounded-full transition-colors relative flex items-center px-1 ${isInternational ? 'bg-[#F37024]' : 'bg-[#E8E0DB]'}`} data-id="element-102">
                  <motion.div className="w-5 h-5 bg-white rounded-full shadow-sm" animate={{
                  x: isInternational ? 20 : 0
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-103" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}